/**
 * SetupWizard — friendly first-run walkthrough that greets a new
 * super_admin instead of dropping them into an unconfigured app
 * with a URL list.
 *
 * States:
 *   • Auto-opens once per user session if `wizard_dismissed=false`
 *     AND at least one required integration is still on
 *     placeholder credentials.
 *   • Dismissable from step 1 (never nag) or from any step (skip
 *     for now). Skipping persists so we don't nag on next login.
 *   • Re-openable from /admin/setup — the sidebar entry.
 *
 * Design notes:
 *   • Each step card shows the CURRENT status detected by the
 *     backend (green check = configured, amber warning = still
 *     placeholder). No credentials are stored client-side.
 *   • The wizard is a MODAL (blocks the app) so an admin can't
 *     accidentally miss it, but the dismiss / skip is always one
 *     click away.
 *   • Copy is intentionally warm — this is the first impression a
 *     new operator has of the tool.
 */
import { useEffect, useState, useCallback } from "react";
import api from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  ShieldCheck, ArrowRight, ArrowLeft, ExternalLink,
  CheckCircle2, AlertTriangle, X, Loader2, Key, Phone, Printer, Sparkles, Radio, Lock,
} from "lucide-react";
import { toast } from "sonner";

const INT_ICONS = {
  twilio: Phone,
  humblefax: Printer,
  emergent_llm: Sparkles,
  elevenlabs: Radio,
};

// Session flag so we only try to auto-open once per browser tab. Persists
// across page reloads via sessionStorage; cleared when the tab closes.
const SESSION_SEEN_KEY = "avida.setup_wizard.seen_this_session";

export default function SetupWizard({ open, onClose, forceOpen = false }) {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [dismissing, setDismissing] = useState(false);

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/setup-wizard/status");
      setStatus(res.data);
    } catch (err) {
      // Silent failure — the wizard just doesn't open. We'd rather
      // hide it than crash the app on first login.
      console.warn("[SetupWizard] status fetch failed", err);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (open) fetchStatus(); }, [open, fetchStatus]);

  const dismiss = async () => {
    setDismissing(true);
    try {
      await api.post("/admin/setup-wizard/dismiss");
      sessionStorage.setItem(SESSION_SEEN_KEY, "1");
      toast.success("Setup wizard dismissed — re-open any time from the sidebar.");
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save preference");
    } finally { setDismissing(false); }
  };

  const skipForNow = () => {
    // Skip is session-scoped: dismissal is deliberate. The next
    // login will still show the wizard if any integration remains
    // on placeholder credentials.
    sessionStorage.setItem(SESSION_SEEN_KEY, "1");
    onClose?.();
  };

  if (!open) return null;

  // Steps: [welcome, ...integrations, security, done]
  const integrations = status?.integrations || [];
  const requiredIntegrations = integrations.filter((i) => !i.optional);
  const allRequiredOk = requiredIntegrations.every((i) => i.configured);
  const allOk = allRequiredOk && status?.jwt_ok;

  // Total step count: welcome + each integration + security + done
  const totalSteps = 1 + integrations.length + 1 + 1;
  const stepIndex = Math.min(step, totalSteps - 1);

  const renderWelcome = () => (
    <div className="space-y-4" data-testid="setup-step-welcome">
      <div className="w-14 h-14 rounded-full bg-yellow-100 border-2 border-yellow-300 flex items-center justify-center">
        <ShieldCheck className="w-7 h-7 text-yellow-600" />
      </div>
      <h2 className="text-2xl font-bold text-slate-900">Welcome to Avida{user?.first_name ? `, ${user.first_name}` : ""} 👋</h2>
      <p className="text-slate-600">
        Before your team uses the phone, fax, and AI features, a few integrations need real credentials.
        This 5-minute walkthrough shows you where each key comes from and how to plug it in.
      </p>
      {status && (
        <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5">
            Current setup snapshot
          </div>
          <ul className="space-y-1 text-sm">
            {integrations.map((i) => (
              <li key={i.key} className="flex items-center gap-2">
                {i.configured
                  ? <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                  : <AlertTriangle className="w-4 h-4 text-amber-500" />}
                <span className={i.configured ? "text-slate-700" : "text-slate-500"}>{i.label}</span>
                {i.optional && <Badge className="text-[10px] bg-slate-100 text-slate-500 border border-slate-200">optional</Badge>}
              </li>
            ))}
            <li className="flex items-center gap-2 mt-2 pt-2 border-t border-slate-200">
              {status.jwt_ok
                ? <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                : <AlertTriangle className="w-4 h-4 text-amber-500" />}
              <span className={status.jwt_ok ? "text-slate-700" : "text-slate-500"}>JWT secret rotated + strong</span>
            </li>
          </ul>
        </div>
      )}
      <p className="text-xs text-slate-500">
        Everything you enter goes into <code className="bg-slate-100 px-1 rounded">/app/backend/.env</code>.
        Values persist across restarts. Rotate any time.
      </p>
    </div>
  );

  const renderIntegration = (i) => {
    const Icon = INT_ICONS[i.key] || Key;
    return (
      <div className="space-y-4" data-testid={`setup-step-${i.key}`}>
        <div className="flex items-center gap-3">
          <div className={"w-12 h-12 rounded-full flex items-center justify-center border-2 " + (i.configured ? "bg-emerald-100 border-emerald-300" : "bg-amber-100 border-amber-300")}>
            <Icon className={"w-6 h-6 " + (i.configured ? "text-emerald-600" : "text-amber-600")} />
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-bold text-slate-900">{i.label}</h2>
            <div className="flex items-center gap-2 mt-0.5">
              {i.configured
                ? <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-200"><CheckCircle2 className="w-3 h-3 mr-1" /> Configured</Badge>
                : <Badge className="bg-amber-100 text-amber-800 border border-amber-200"><AlertTriangle className="w-3 h-3 mr-1" /> Placeholder — needs real key</Badge>}
              {i.optional && <Badge className="text-[10px] bg-slate-100 text-slate-500 border border-slate-200">optional</Badge>}
            </div>
          </div>
        </div>

        <p className="text-sm text-slate-700 leading-relaxed">{i.docs_hint}</p>

        <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 space-y-1">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1">
            .env keys to set
          </div>
          {i.env_vars.map((v) => (
            <code key={v} className="block bg-white border border-slate-200 rounded px-2 py-1 text-[12px] font-mono text-slate-800">
              {v}=<span className="text-slate-400">paste-value-here</span>
            </code>
          ))}
        </div>

        <a
          href={i.provider_url}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-blue-600 hover:text-blue-700"
          data-testid={`setup-provider-link-${i.key}`}
        >
          Open {i.label.split(" ")[0]} console <ExternalLink className="w-3.5 h-3.5" />
        </a>

        {!i.configured && (
          <p className="text-[12px] text-slate-500 pt-2 border-t border-slate-200">
            After you paste values into <code className="bg-slate-100 px-1 rounded">.env</code>, restart the backend
            (<code className="bg-slate-100 px-1 rounded">sudo supervisorctl restart backend</code>). The banner will
            update on your next login. This step is safe to skip while you gather keys.
          </p>
        )}
      </div>
    );
  };

  const renderSecurity = () => (
    <div className="space-y-4" data-testid="setup-step-security">
      <div className={"w-12 h-12 rounded-full flex items-center justify-center border-2 " + (status?.jwt_ok ? "bg-emerald-100 border-emerald-300" : "bg-red-100 border-red-300")}>
        <Lock className={"w-6 h-6 " + (status?.jwt_ok ? "text-emerald-600" : "text-red-600")} />
      </div>
      <h2 className="text-xl font-bold text-slate-900">Security housekeeping</h2>
      <p className="text-sm text-slate-700">
        The most important knob in production. If a JWT secret leaks, every user session can be forged.
      </p>
      <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 space-y-2">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Checklist</div>
        <ul className="space-y-2 text-sm">
          <li className="flex items-start gap-2">
            {status?.jwt_ok
              ? <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5" />
              : <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5" />}
            <div>
              <div className="font-semibold text-slate-800">JWT_SECRET (64 random chars)</div>
              <div className="text-slate-500 text-xs">
                {status?.jwt_ok
                  ? "Your secret looks strong. Rotate any time from .env — every user will be logged out."
                  : "Generate one with:"}
              </div>
              {!status?.jwt_ok && (
                <code className="block mt-1 bg-white border border-slate-200 rounded px-2 py-1 text-[11px] font-mono text-slate-800">
                  {`python3 -c "import secrets; print(secrets.token_urlsafe(48))"`}
                </code>
              )}
            </div>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5" />
            <div>
              <div className="font-semibold text-slate-800">Change your admin password</div>
              <div className="text-slate-500 text-xs">
                Profile → Change password. The system now rotates your session on password change so old tokens die instantly.
              </div>
            </div>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5" />
            <div>
              <div className="font-semibold text-slate-800">Watch the audit log</div>
              <div className="text-slate-500 text-xs">
                Every PHI mutation is captured. Bookmark the sidebar <b>Audit Trail</b> for surveyor visits.
              </div>
            </div>
          </li>
        </ul>
      </div>
    </div>
  );

  const renderDone = () => (
    <div className="space-y-4 text-center" data-testid="setup-step-done">
      <div className={"mx-auto w-14 h-14 rounded-full flex items-center justify-center border-2 " + (allOk ? "bg-emerald-100 border-emerald-300" : "bg-amber-100 border-amber-300")}>
        {allOk
          ? <CheckCircle2 className="w-7 h-7 text-emerald-600" />
          : <AlertTriangle className="w-7 h-7 text-amber-600" />}
      </div>
      <h2 className="text-2xl font-bold text-slate-900">{allOk ? "You're all set 🎉" : "You can finish setup later"}</h2>
      <p className="text-slate-600 max-w-md mx-auto">
        {allOk
          ? "Every integration is configured and your JWT secret is strong. Dismiss this walkthrough and dive in."
          : "Come back to this walkthrough from the sidebar → Setup any time. The banner will disappear once every integration reports green."}
      </p>
      <div className="pt-3 border-t border-slate-200 text-sm text-slate-500 space-y-1">
        <p>Quick jumps:</p>
        <p><a href="/admin/health" className="text-blue-600 hover:underline">System Health</a> · <a href="/admin/audit-log" className="text-blue-600 hover:underline">Audit Trail</a> · <a href="/admin" className="text-blue-600 hover:underline">Admin Panel</a></p>
      </div>
    </div>
  );

  const renderStep = () => {
    if (stepIndex === 0) return renderWelcome();
    const intIdx = stepIndex - 1;
    if (intIdx < integrations.length) return renderIntegration(integrations[intIdx]);
    if (stepIndex === totalSteps - 2) return renderSecurity();
    return renderDone();
  };

  return (
    <div className="fixed inset-0 z-[80] bg-slate-900/60 flex items-center justify-center p-4" data-testid="setup-wizard-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200 flex items-center gap-3">
          <div className="flex-1">
            <div className="text-[10px] font-bold uppercase tracking-widest text-blue-600">Setup Wizard</div>
            <div className="text-xs text-slate-500">
              Step {stepIndex + 1} of {totalSteps}
              {forceOpen && <span className="ml-2">· Re-opened from sidebar</span>}
            </div>
          </div>
          <button
            onClick={skipForNow}
            className="text-slate-400 hover:text-slate-700 transition-colors"
            data-testid="setup-wizard-close"
            aria-label="Close for now"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Progress */}
        <div className="h-1 bg-slate-100">
          <div
            className="h-full bg-gradient-to-r from-blue-500 to-emerald-500 transition-all duration-300"
            style={{ width: `${((stepIndex + 1) / totalSteps) * 100}%` }}
          />
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6" data-testid="setup-wizard-body">
          {loading && !status ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
            </div>
          ) : renderStep()}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-slate-200 flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            disabled={stepIndex === 0 || dismissing}
            data-testid="setup-wizard-back"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={skipForNow}
            className="text-slate-500"
            disabled={dismissing}
            data-testid="setup-wizard-skip"
          >
            Skip for now
          </Button>
          <div className="flex-1" />
          {stepIndex === totalSteps - 1 ? (
            <Button
              size="sm"
              onClick={dismiss}
              disabled={dismissing}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="setup-wizard-finish"
            >
              {dismissing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
              {allOk ? "Finish setup" : "Dismiss for now"}
            </Button>
          ) : (
            <Button
              size="sm"
              onClick={() => setStep((s) => Math.min(totalSteps - 1, s + 1))}
              disabled={dismissing || (loading && !status)}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="setup-wizard-next"
            >
              Next <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * SetupWizardAutoOpener — mount once in the app shell. Checks
 * `/admin/setup-wizard/status` on login; if the wizard isn't
 * dismissed AND at least one required integration is placeholder,
 * shows the modal. Never shows more than once per browser tab
 * (session flag).
 */
export function SetupWizardAutoOpener() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!user) return;
    // Only admins care about setup — this endpoint would 403 for others.
    if (!["super_admin", "admin"].includes(user.role)) return;
    // One shot per tab so we don't reopen after every route change.
    if (sessionStorage.getItem(SESSION_SEEN_KEY) === "1") return;
    // Don't stack a second modal on top of the /admin/setup page —
    // that page already renders SetupWizard directly.
    if (typeof window !== "undefined" && window.location.pathname === "/admin/setup") return;

    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/admin/setup-wizard/status");
        if (cancelled) return;
        const ints = res.data?.integrations || [];
        const requiredMissing = ints.some((i) => !i.optional && !i.configured);
        const shouldOpen = !res.data?.wizard_dismissed && (requiredMissing || !res.data?.jwt_ok);
        if (shouldOpen) setOpen(true);
        else sessionStorage.setItem(SESSION_SEEN_KEY, "1");
      } catch { /* silent — never break login on wizard failure */ }
    })();
    return () => { cancelled = true; };
  }, [user]);

  return <SetupWizard open={open} onClose={() => setOpen(false)} />;
}
